import React, { memo, useState, useCallback, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'

/* ── Styled Components ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.15);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Modal = styled.div`
  background: rgba(229, 228, 226, 0.98);
  border: 2px solid #EB7C0C;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(235, 124, 12, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  width: 80vw;
  height: 80vh;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid #f0e6dc;
  background: linear-gradient(to bottom, #fdf8f4, rgba(229, 228, 226, 0.98));
`

const Title = styled.span`
  font-family: coresanscr65, sans-serif;
  font-size: 13px;
  color: #1F194C;
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 18px;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover { background: #f5f5f5; color: #333; }
`

const ImageArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 16px;
  cursor: grab;
  &:active { cursor: grabbing; }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  border-top: 1px solid #f0e6dc;
  background: linear-gradient(to top, #fdf8f4, rgba(229, 228, 226, 0.98));
  gap: 4px;
`

const ZoomBtn = styled.button`
  background: none;
  border: 1px solid #e0d5ca;
  color: #EB7C0C;
  font-size: 16px;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  &:hover { background: #EB7C0C; color: #fff; border-color: #EB7C0C; }
  &:disabled { opacity: 0.3; cursor: default; &:hover { background: none; color: #EB7C0C; border-color: #e0d5ca; } }
`

const ZoomLabel = styled.button`
  background: none;
  border: none;
  color: #1F194C;
  font-size: 12px;
  font-family: coresanscr55, sans-serif;
  min-width: 48px;
  text-align: center;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover { background: #f0e6dc; }
`

/* ── Component ── */

interface ImageModalProps {
  src: string
  alt?: string
  onClose: () => void
}

const MIN_ZOOM = 0.5
const MAX_ZOOM = 5
const ZOOM_STEP = 0.25

const ImageModal = ({ src, alt, onClose }: ImageModalProps) => {
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const isDragging = useRef(false)
  const lastPos = useRef({ x: 0, y: 0 })

  const resetView = useCallback(() => {
    setZoom(1)
    setOffset({ x: 0, y: 0 })
  }, [])

  const zoomIn = useCallback(() => {
    setZoom(z => Math.min(z + ZOOM_STEP, MAX_ZOOM))
  }, [])

  const zoomOut = useCallback(() => {
    setZoom(z => {
      const next = Math.max(z - ZOOM_STEP, MIN_ZOOM)
      if (next <= 1) setOffset({ x: 0, y: 0 })
      return next
    })
  }, [])

  const onWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) { zoomIn() } else { zoomOut() }
  }, [zoomIn, zoomOut])

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (zoom <= 1) return
    isDragging.current = true
    lastPos.current = { x: e.clientX, y: e.clientY }
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [zoom])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return
    const dx = e.clientX - lastPos.current.x
    const dy = e.clientY - lastPos.current.y
    lastPos.current = { x: e.clientX, y: e.clientY }
    setOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }))
  }, [])

  const onPointerUp = useCallback(() => {
    isDragging.current = false
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>Patient Image</Title>
          <CloseBtn onClick={onClose} aria-label="Close image viewer">✕</CloseBtn>
        </Header>

        <ImageArea
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            style={{
              maxWidth: '75vw',
              maxHeight: 'calc(80vh - 140px)',
              objectFit: 'contain',
              userSelect: 'none',
              borderRadius: '4px',
              transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
              transition: isDragging.current ? 'none' : 'transform 0.15s ease-out',
            }}
          />
        </ImageArea>

        <Footer>
          <ZoomBtn onClick={zoomOut} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">−</ZoomBtn>
          <ZoomLabel onClick={resetView} aria-label="Reset zoom">{Math.round(zoom * 100)}%</ZoomLabel>
          <ZoomBtn onClick={zoomIn} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">+</ZoomBtn>
        </Footer>
      </Modal>
    </Overlay>,
    document.body
  )
}

ImageModal.defaultProps = {
  alt: '',
}

export default memo(ImageModal)
